import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import AssistantChat from "../components/AssistantChat";
import VoiceMode from "../components/VoiceMode";
import UsageMeter from "../components/UsageMeter";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Package, PenTool, Facebook, MessageSquare,
  Image as ImageIcon, FlaskConical, ArrowRight, ChevronDown, Lock,
  CheckCircle2, Loader2,
} from "lucide-react";

// AI Center — home for every generative tool the Institute ships. The
// primary tile is Warren AI (Emergent LLM Key). The remaining six cards
// belong to the "AI Studio" — a $25/mo add-on that gates content creation.
export const AI_TOOLS = [
  { key: "packaging-designer",  slug: "packaging-designer",  name: "Packaging Designer",
    tagline: "Draft packaging direction from your brand brief.",
    icon: Package,        hue: "pink",   live: true  },
  { key: "logo-designer",       slug: "logo-designer",       name: "Logo Designer",
    tagline: "Explore logo concepts iterated on your positioning notes.",
    icon: PenTool,        hue: "cyan",   live: true  },
  { key: "meta-ads-manager",    slug: "meta-ads-manager",    name: "Meta Ads Manager",
    tagline: "Plan Facebook & Instagram campaigns with AI copy + targeting.",
    icon: Facebook,       hue: "yellow", live: true  },
  { key: "reddit-ads-manager",  slug: "reddit-ads-manager",  name: "Reddit Ads Manager",
    tagline: "Community-aware Reddit ads with subreddit + creative picks.",
    icon: MessageSquare,  hue: "pink",   live: true  },
  { key: "ads-designer",        slug: "ads-designer",        name: "Ads Designer",
    tagline: "Generate on-brand ad creatives across every format.",
    icon: ImageIcon,      hue: "cyan",   live: true  },
  { key: "formulation-creator", slug: "formulation-creator", name: "Formulation Creator",
    tagline: "Get first-pass ingredient decks + benchmark specs.",
    icon: FlaskConical,   hue: "yellow", live: true  },
];

const HUE = {
  pink:   { ring: "ring-primary/25",       icon: "text-primary bg-primary/10",    chip: "bg-primary/10 text-primary" },
  cyan:   { ring: "ring-sky-400/25",       icon: "text-sky-500 bg-sky-400/10",     chip: "bg-sky-400/10 text-sky-500" },
  yellow: { ring: "ring-pink-400/25",     icon: "text-pink-600 bg-pink-400/15", chip: "bg-pink-400/15 text-pink-600" },
};

export default function AICenter() {
  const [params, setParams] = useSearchParams();
  const [chatOpen, setChatOpen] = useState(false);
  const [status, setStatus] = useState(null); // {active, allowance, used, current_period_end, ...}
  const [subscribing, setSubscribing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [forbiddenTools, setForbiddenTools] = useState(null); // null = loading; Set = loaded

  const loadStatus = () =>
    api.get("/ai-addon/status").then((r) => setStatus(r.data)).catch(() => setStatus({ active: false }));

  useEffect(() => { loadStatus(); }, []);

  // Filter AI tools based on the admin's per-plan configuration in the
  // Menu Directory. Tools flagged as disabled or restricted to plans the
  // user doesn't hold are hidden entirely. Fail-closed: if the visibility
  // fetch errors, hide every tool rather than leak restricted ones.
  useEffect(() => {
    api.get("/menu-visibility").then((r) => {
      const forbidden = new Set(((r.data?.children_allowed || {})["nav-ai-center"]) || []);
      const overrides = (r.data?.children_overrides || {})["nav-ai-center"] || {};
      for (const [cid, entry] of Object.entries(overrides)) {
        if (entry?.disabled) forbidden.add(cid);
      }
      setForbiddenTools(forbidden);
    }).catch(() => setForbiddenTools(new Set(AI_TOOLS.map((t) => t.slug))));
  }, []);

  const visibleTools = forbiddenTools === null
    ? []
    : AI_TOOLS.filter((t) => !forbiddenTools.has(t.slug));
  const toolsLoading = forbiddenTools === null;

  // If Stripe redirected us back with a session id, poll payment status a
  // few times and refresh the add-on state so the UI flips to active.
  useEffect(() => {
    const sid = params.get("ai_addon_session");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("AI Studio add-on activated");
          await loadStatus();
          setParams({}, { replace: true });
          return;
        }
      } catch { /* ignore */ }
      if (tries < 8) setTimeout(poll, 1500);
      else { toast.info("Payment is still processing — refresh in a minute."); setParams({}, { replace: true }); }
    };
    poll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const r = await api.post("/ai-addon/checkout", { origin_url: window.location.origin });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatError(e)); setSubscribing(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your AI Studio add-on? You'll keep access until the end of the current billing period.")) return;
    setCancelling(true);
    try { await api.post("/ai-addon/cancel"); toast.success("Add-on will end at the current period close"); loadStatus(); }
    catch (e) { toast.error(formatError(e)); }
    finally { setCancelling(false); }
  };

  const resume = async () => {
    setCancelling(true);
    try { await api.post("/ai-addon/resume"); toast.success("Add-on renewal resumed"); loadStatus(); }
    catch (e) { toast.error(formatError(e)); }
    finally { setCancelling(false); }
  };

  const isActive = !!status?.active;
  const endsOn = status?.current_period_end ? new Date(status.current_period_end).toLocaleDateString() : null;

  return (
    <AppShell title="AI Center">
      <div className="w-full">
        <PageHero
          eyebrow="AI Center"
          icon={Sparkles}
          title={<>Every AI tool your <span className="text-grad">CPG brand</span> needs</>}
          subtitle="Talk to Morgan free (included in every plan). Unlock the AI Studio for $25/mo to design packaging, launch ads, and explore formulations."
        />

        {/* Primary: Morgan (always free / bundled) */}
        <section className="mt-10">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h2 className="font-display text-2xl">Morgan — Course Assistant</h2>
            <Link to="/assistant" data-testid="ai-center-open-full-assistant"
              className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
              Full page <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Course-aware for the 16 modules and connected to Claude &amp; ChatGPT
            for open CPG questions. Speaker icons play answers in Morgan's voice.
          </p>

          <div className="mt-6">
            <VoiceMode />
          </div>

          <button
            onClick={() => setChatOpen((o) => !o)}
            data-testid="ai-center-toggle-chat"
            className="mt-6 w-full text-left surface p-5 hover:ring-2 hover:ring-primary/20 transition-all inline-flex items-center justify-between gap-3"
          >
            <span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Chat with Warren AI
              </span>
              <span className="block font-display text-lg">
                {chatOpen ? "Chat is open below" : "Tap to start a conversation"}
              </span>
            </span>
            <ChevronDown className={"w-5 h-5 text-primary transition-transform " + (chatOpen ? "rotate-180" : "")} />
          </button>

          {chatOpen && (
            <div className="mt-4" data-testid="ai-center-chat-wrap">
              <AssistantChat variant="page" />
              <div className="mt-6"><UsageMeter /></div>
            </div>
          )}
        </section>

        {/* AI Studio header / subscription state */}
        <section className="mt-14" data-testid="ai-studio-section">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h2 className="font-display text-2xl">AI Studio</h2>
            {isActive && (
              <span className="chip-amber inline-flex items-center gap-1" data-testid="ai-studio-status">
                <CheckCircle2 className="w-3 h-3" /> Add-on active
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Purpose-built tools for every stage — from concept to launch. Content-creation
            tools require the AI Studio add-on ($25/mo, unlimited chat, 50 image generations
            included; overage billed monthly).
          </p>

          {status === null ? (
            <div className="mt-6 surface p-6 flex items-center gap-3 text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking your subscription…
            </div>
          ) : !isActive ? (
            <div className="mt-6 surface p-6 flex items-start justify-between gap-4 flex-wrap" data-testid="ai-studio-cta-panel">
              <div className="min-w-0 max-w-xl">
                <p className="font-display text-2xl">Unlock the AI Studio</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Every tool below is included for $25/mo. Cancel anytime — you'll keep
                  access until the end of your billing period.
                </p>
                <ul className="mt-3 text-xs text-muted-foreground grid gap-1">
                  <li>· 100 AI chat generations per month</li>
                  <li>· 50 image generations per month</li>
                  <li>· Metered overage billed on the first of the next month</li>
                </ul>
              </div>
              <button
                onClick={subscribe}
                disabled={subscribing}
                data-testid="ai-studio-subscribe"
                className="btn-primary text-sm disabled:opacity-60 inline-flex items-center gap-1.5"
              >
                {subscribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Subscribe · $25/mo
              </button>
            </div>
          ) : (
            <div className="mt-6 surface p-6" data-testid="ai-studio-usage-panel">
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-display text-xl">This month's usage</p>
                  {endsOn && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {status.cancel_at_period_end
                        ? `Access ends on ${endsOn}`
                        : `Renews on ${endsOn}`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {status.cancel_at_period_end ? (
                    <button onClick={resume} disabled={cancelling} data-testid="ai-studio-resume"
                      className="btn-primary text-xs disabled:opacity-60">
                      Resume renewal
                    </button>
                  ) : (
                    <button onClick={cancel} disabled={cancelling} data-testid="ai-studio-cancel"
                      className="text-xs font-semibold border border-shell-border rounded-full px-4 py-2 hover:bg-muted">
                      Cancel add-on
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <UsageBar label="AI chat" used={status.used?.chats || 0} cap={status.allowance?.chats || 0} testid="ai-usage-chats" />
                <UsageBar label="Image gen" used={status.used?.images || 0} cap={status.allowance?.images || 0} testid="ai-usage-images" />
              </div>
            </div>
          )}

          {/* Grid of tools — locked when subscription inactive */}
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="ai-center-tools-grid">
            {toolsLoading ? (
              <div className="col-span-full flex items-center gap-2 text-sm text-muted-foreground" data-testid="ai-center-tools-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading available tools…
              </div>
            ) : (
              <>
                {visibleTools.map((tool) => (
                  <AIToolCard key={tool.key} tool={tool} locked={!isActive} />
                ))}
                {visibleTools.length === 0 && (
                  <p className="col-span-full text-sm text-muted-foreground italic" data-testid="ai-center-tools-empty">
                    No AI Studio tools are available on your current plan. Upgrade to unlock more tools.
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function UsageBar({ label, used, cap, testid }) {
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const over = cap && used > cap;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-semibold">{label}</span>
        <span className={over ? "text-primary font-semibold" : "text-muted-foreground"}>
          {used} / {cap}
        </span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={"h-full rounded-full " + (over ? "bg-primary" : "bg-shell-side")}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function AIToolCard({ tool, locked }) {
  const Icon = tool.icon;
  const hue = HUE[tool.hue] || HUE.pink;
  const isLive = !!tool.live;
  return (
    <Link
      to={`/ai/${tool.slug}`}
      data-testid={`ai-tool-${tool.key}`}
      className={"group relative surface p-6 flex flex-col hover:ring-2 " + hue.ring + " transition-all"}
    >
      <div className="flex items-start justify-between">
        <div className={"w-11 h-11 rounded-xl grid place-items-center " + hue.icon}>
          <Icon className="w-5 h-5" />
        </div>
        {locked ? (
          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-shell-border text-muted-foreground inline-flex items-center gap-1">
            <Lock className="w-3 h-3" /> Add-on
          </span>
        ) : isLive ? (
          <span className={"text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full " + hue.chip + " inline-flex items-center gap-1"}>
            <CheckCircle2 className="w-3 h-3" /> Live
          </span>
        ) : (
          <span className={"text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full " + hue.chip}>
            Coming soon
          </span>
        )}
      </div>
      <p className="mt-5 font-display text-xl">{tool.name}</p>
      <p className="mt-1 text-sm text-muted-foreground flex-1">{tool.tagline}</p>
      <span className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-primary group-hover:underline">
        {locked ? "Preview + subscribe" : (isLive ? "Open tool" : "Preview + waitlist")} <ArrowRight className="w-3 h-3" />
      </span>
    </Link>
  );
}
